import LogRocket from 'logrocket'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import appInsights from '../AppInsights'
import { toast } from '../components/CustomToastMessage/toast'
import { RECAPTCHA_CLIENT_KEY } from '../config/config'
import { MDVIP_ACCOUNT_TIMEOUT_ERROR_MESSAGE } from '../Constants'
import { ReinstatementContactInformationDocument, ReinstatementEligibilityDocument } from '../generated/hooks'
import { authenticate, AuthenticateParams, AuthenticateResponse } from '../service/authenticate'
import { formatServerDateString } from '../utils/dateUtils'
import { generateRouteWithInvoice, removeInvoiceId } from '../utils/invoiceUtils'
import { ContactInfoOutcome, mapContactInfoQueryResult, mapNonAcceptedPlans } from '../utils/reinstatementUtils'
import { getApolloClient } from './useApolloClient'
import { useJwtExpired } from './useJwtExpired'
import usePaymentDataContext from './usePaymentData'

const AUTHENTICATED_DATA_STORAGE_KEY = 'authenticated_data_v1'

type ReinstatementGateOutcome = { eligible: true } | { eligible: false; error?: unknown }

/**
 * Runs the reinstatement-eligibility query and returns the outcome. No side effects on
 * React state — the caller decides what to do based on the result. Network/Apollo errors
 * resolve to { eligible: false, error } rather than rejecting, so the caller can branch
 * once instead of needing both .then and .catch.
 */
// Client-side abort for middleware queries — keeps the verify form responsive if
// the upstream takes too long, without waiting for Cloudflare's gateway timeout.
const MIDDLEWARE_TIMEOUT_MS = 15_000

const checkReinstatementEligibility = async (token: string): Promise<ReinstatementGateOutcome> => {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), MIDDLEWARE_TIMEOUT_MS)
  try {
    const result = await getApolloClient(token).query({
      query: ReinstatementEligibilityDocument,
      fetchPolicy: 'no-cache',
      context: { fetchOptions: { signal: controller.signal } },
    })
    clearTimeout(timeoutId)
    return { eligible: result.data?.reinstatementEligibility?.eligible === true }
  } catch (error) {
    clearTimeout(timeoutId)
    return { eligible: false, error }
  }
}

/**
 * Sibling helper to checkReinstatementEligibility. Runs the contact-info query as the
 * second auth gate and resolves to a discriminated outcome. Network/Apollo errors resolve
 * to { kind: 'network', error } rather than rejecting, so the caller can branch once.
 * Caller treats any non-success outcome (and success with null msrp) as a blocking
 * failure — toast + reset auth, stay on /verify.
 */
const fetchReinstatementContactInformation = async (token: string): Promise<ContactInfoOutcome> => {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), MIDDLEWARE_TIMEOUT_MS)
  try {
    const result = await getApolloClient(token).query({
      query: ReinstatementContactInformationDocument,
      fetchPolicy: 'no-cache',
      context: { fetchOptions: { signal: controller.signal } },
    })
    clearTimeout(timeoutId)
    return mapContactInfoQueryResult(result.data?.reinstatementContactInformation)
  } catch (error) {
    clearTimeout(timeoutId)
    return { kind: 'network', error }
  }
}

type AuthenticatedData = {
  token: string
  mdvipId: string
  dob: string
  balance: string
  estimatedPaidThrough: string
  autoPay: boolean
  isReinstatement: boolean
  firstName: string
  lastName: string
  email: string | null
  phone: string | null
  physicianName: string | null
}

const defaultAuthenticatedData: AuthenticatedData = {
  token: '',
  mdvipId: '',
  dob: '',
  balance: '',
  estimatedPaidThrough: '',
  autoPay: false,
  isReinstatement: false,
  firstName: '',
  lastName: '',
  email: null,
  phone: null,
  physicianName: null,
}

const AuthenticateContext = createContext<{
  token: string | null
  authenticatedMember: AuthenticateResponse['member'] | null
  loading: boolean
  setLoading: (loading: boolean) => void
  setParams: (params: AuthenticateParams | null) => void
  authenticatedData: AuthenticatedData
  isReinstatement: boolean
  clearAuthenticatedData: () => void
  persistAuthenticatedData: (next: AuthenticatedData) => void
}>({
  token: null,
  authenticatedMember: null,
  loading: false,
  setLoading: () => {},
  setParams: () => {},
  authenticatedData: defaultAuthenticatedData,
  isReinstatement: false,
  clearAuthenticatedData: () => {},
  persistAuthenticatedData: () => {},
})

export const AuthenticateProvider = ({ children }: { children: React.ReactNode }) => {
  const [token, setToken] = useState<string | null>(null)
  const [authenticatedMember, setAuthenticatedMember] = useState<AuthenticateResponse['member'] | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [params, setParams] = useState<AuthenticateParams | null>(null)
  const navigateAfterAuthRef = useRef(false)
  const { clearPaymentData, setMsrp, setNonAcceptedPlans } = usePaymentDataContext()

  const [authenticatedData, setAuthenticatedData] = useState<AuthenticatedData>(() => {
    try {
      const raw = sessionStorage.getItem(AUTHENTICATED_DATA_STORAGE_KEY)
      return raw ? (JSON.parse(raw) as AuthenticatedData) : defaultAuthenticatedData
    } catch {
      return defaultAuthenticatedData
    }
  })

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const persistAuthenticatedData = useCallback((next: AuthenticatedData) => {
    sessionStorage.setItem(AUTHENTICATED_DATA_STORAGE_KEY, JSON.stringify(next))
    setAuthenticatedData(next)
  }, [])

  const clearAuthenticatedData = useCallback(() => {
    sessionStorage.removeItem(AUTHENTICATED_DATA_STORAGE_KEY)
    setAuthenticatedData(defaultAuthenticatedData)
    clearPaymentData()
  }, [clearPaymentData])

  useEffect(() => {
    setToken(null)

    if (!params || (RECAPTCHA_CLIENT_KEY && !params.recaptchaToken)) {
      setLoading(false)
      return
    }

    setAuthenticatedMember(null)
    setLoading(true)

    authenticate({
      ...params,
      dob: formatServerDateString(params.dob),
    })
      .then((response) => {
        setToken(response.token)
        setAuthenticatedMember(response.member)
      })
      .catch((error: any) => {
        setToken(null)
        setAuthenticatedMember(null)

        const keysToIgnore = ['recaptchaToken', 'onAuthenticateError']

        const filteredParams = Object.fromEntries(Object.entries(params).filter(([key]) => !keysToIgnore.includes(key)))

        appInsights.trackException({
          exception: error,
          properties: {
            operation: 'authenticate',
            variables: filteredParams,
          },
        })

        if (params?.onAuthenticateError) {
          params.onAuthenticateError(error.message)
        }

        setLoading(false)
      })
  }, [params])

  // Only fires when token/authenticatedMember change (i.e. after API responds).
  // params is intentionally excluded from deps: including it would cause this effect to fire
  // with new params but stale token/member from the previous auth, persisting wrong data.
  useEffect(() => {
    if (!params || !token || !authenticatedMember) return

    LogRocket.identify(authenticatedMember.id)

    const isReinstatement = authenticatedMember.isInactive

    // PII fields (firstName/lastName/email/phone/physicianName) start at the
    // AuthenticatedData defaults (empty/null) — they're populated only by the
    // reinstatementContactInformation overlay below for reinstating members.
    // Non-reinstating members never display these fields.
    const nextAuthData: AuthenticatedData = {
      ...defaultAuthenticatedData,
      token,
      mdvipId: authenticatedMember.mdvipId,
      dob: params.dob,
      balance: String(authenticatedMember.balance),
      estimatedPaidThrough: authenticatedMember.estimatedPaidThrough,
      autoPay: authenticatedMember.autoPay,
      isReinstatement,
    }

    if (!isReinstatement) {
      persistAuthenticatedData(nextAuthData)
      navigateAfterAuthRef.current = true
      return
    }

    // Reinstatement: gate on eligibility AND contact-info before persisting/navigating.
    // Both must produce complete data (incl. non-null msrp) before user reaches /payment.
    // Failure on either gate produces the same UX as ineligibility (toast + reset, stay on /verify).
    let cancelled = false

    checkReinstatementEligibility(token).then(async (eligibilityOutcome) => {
      if (cancelled) return

      if (!eligibilityOutcome.eligible) {
        if (eligibilityOutcome.error !== undefined) {
          appInsights.trackException({
            exception:
              eligibilityOutcome.error instanceof Error
                ? eligibilityOutcome.error
                : new Error(String(eligibilityOutcome.error)),
            properties: { operation: 'reinstatementEligibility' },
          })
        }
        clearAuthenticatedData()
        setToken(null)
        setAuthenticatedMember(null)
        setParams(null)
        setLoading(false)
        params?.onAuthenticateError?.(null)
        return
      }

      const contactOutcome = await fetchReinstatementContactInformation(token)
      if (cancelled) return

      if (contactOutcome.kind === 'success') {
        const { data } = contactOutcome
        if (data.msrpAmount !== null) {
          persistAuthenticatedData({
            ...nextAuthData,
            firstName: data.firstName ?? '',
            lastName: data.lastName ?? '',
            email: data.email,
            phone: data.phone,
            physicianName: data.physicianName,
          })
          setMsrp(data.msrpAmount)
          setNonAcceptedPlans(mapNonAcceptedPlans(data.nonAcceptedPaymentPlans))
          navigateAfterAuthRef.current = true
          return
        }
        // success-with-null-msrp: incomplete data, treat as a domain failure (no log).
      }

      // Failure path — same UX as ineligibility. Network errors are logged; domain
      // failures (not-found, success-with-null-msrp) are silent.
      if (contactOutcome.kind === 'network') {
        appInsights.trackException({
          exception:
            contactOutcome.error instanceof Error ? contactOutcome.error : new Error(String(contactOutcome.error)),
          properties: { operation: 'reinstatementContactInformation' },
        })
      }
      clearAuthenticatedData()
      setToken(null)
      setAuthenticatedMember(null)
      setParams(null)
      setLoading(false)
      params?.onAuthenticateError?.(null)
    })

    return () => {
      cancelled = true
    }
    // params is read here but excluded from deps for the same reason as above. The state
    // setters (setToken, setAuthenticatedMember, setParams, setLoading) are stable React
    // setters; persistAuthenticatedData and clearAuthenticatedData are the useCallbacks we depend on explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, authenticatedMember, persistAuthenticatedData, clearAuthenticatedData])

  // Navigate only after authenticatedData has committed — avoids flash of stale isReinstatement.
  useEffect(() => {
    if (navigateAfterAuthRef.current) {
      navigateAfterAuthRef.current = false
      navigate('/payment')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticatedData])

  // Handle Token expiration
  const expiredRoute = generateRouteWithInvoice('/', 'expired=true')

  useJwtExpired(() => {
    setToken(null)
    setAuthenticatedMember(null)
    setLoading(false)
    setParams(null)
    clearAuthenticatedData()
    removeInvoiceId(dispatch)
    navigate(expiredRoute)
    toast(MDVIP_ACCOUNT_TIMEOUT_ERROR_MESSAGE)
  }, token)

  const value = useMemo(
    () => ({
      token,
      authenticatedMember,
      loading,
      setLoading,
      setParams,
      authenticatedData,
      isReinstatement: authenticatedData.isReinstatement,
      clearAuthenticatedData,
      persistAuthenticatedData,
    }),
    [
      token,
      authenticatedMember,
      loading,
      authenticatedData,
      persistAuthenticatedData,
      clearAuthenticatedData,
      setLoading,
    ],
  )

  return <AuthenticateContext.Provider value={value}>{children}</AuthenticateContext.Provider>
}

export default function useAuthenticateContext() {
  return useContext(AuthenticateContext)
}
